import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocation } from 'react-router-dom';
import {
  SEO_DEFAULT_OG_IMAGE,
  SEO_DEFAULT_OG_IMAGE_HEIGHT,
  SEO_DEFAULT_OG_IMAGE_TYPE,
  SEO_DEFAULT_OG_IMAGE_WIDTH,
  SEO_LOCALE_URL_STRATEGY,
  SITE_NAME,
  SITE_URL,
  buildAbsoluteUrl,
  getRouteSeoDefaults,
  getSeoUrls,
  inferPageSchemaType,
  buildDerivedSeoDefaults,
  inferImageMimeType,
} from '@/lib/seo';
import { getLogicalPath, localizePath } from '@/lib/localeRouting';
import { buildHomeAgentReadinessNodes } from '@/lib/agentReadinessSchema';
import {
  buildCustomOgImageAlt,
  buildDefaultOgImageAlt,
  buildTemplateOgImageAlt,
} from '@/lib/ogImageAlt.shared.js';
import {
  OG_TEMPLATE_IMAGE_HEIGHT,
  OG_TEMPLATE_IMAGE_TYPE,
  OG_TEMPLATE_IMAGE_WIDTH,
  hasTemplateOgImage,
  templateOgImagePath,
} from '@/lib/ogTemplateImage.shared.js';
import {
  buildOrganizationSchema,
  buildProfessionalServiceSchema,
  buildWebPageSchema,
  buildWebSiteSchema,
  buildSeoGraph,
  localizeJsonLdUrls,
  type JsonLd,
} from '@/lib/schema';
interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  type?: 'website' | 'article';
  jsonLd?: JsonLd | JsonLd[];
  /** `'follow'` keeps link equity flowing from thin listing pages (`noindex, follow`). */
  noindex?: boolean | 'follow';
  authorName?: string;
  /** Override hreflang logical paths per locale (e.g. blog TR slugs). */
  localeAlternatePaths?: Partial<Record<'tr' | 'en', string>>;
  ogImageAlt?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageType?: string;
  publishedTime?: string;
  modifiedTime?: string;
  articleAuthorUrl?: string;
}

const googleSiteVerification =
  import.meta.env.VITE_GOOGLE_SITE_VERIFICATION ||
  import.meta.env.NEXT_PUBLIC_GOOGLE_SITE_VERIFICATION;
const bingSiteVerification =
  import.meta.env.VITE_BING_SITE_VERIFICATION ||
  import.meta.env.NEXT_PUBLIC_BING_SITE_VERIFICATION;

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage,
  type = 'website',
  jsonLd,
  noindex = false,
  authorName,
  localeAlternatePaths,
  ogImageAlt,
  ogImageWidth,
  ogImageHeight,
  ogImageType,
  publishedTime,
  modifiedTime,
  articleAuthorUrl,
}: SEOHeadProps) => {
  const { lang } = useLanguage();
  const location = useLocation();
  const logicalPath = getLogicalPath(canonical ?? location.pathname ?? '/');
  const routeDefaults =
    getRouteSeoDefaults(logicalPath, lang) ??
    buildDerivedSeoDefaults(logicalPath, lang);
  const defaultTitle = routeDefaults?.title ?? (lang === 'tr'
    ? 'AI, SaaS ve Özel Yazılım Geliştirme | Figtures'
    : 'Figtures | AI, SaaS & Custom Software Development');
  const defaultDesc = routeDefaults?.description ?? (lang === 'tr'
    ? 'Fikirden canlı ürüne: AI geliştirme, SaaS/MVP, özel yazılım ve mobil uygulama. İstanbul merkezli product studio; discovery’dan production’a tek ekip.'
    : 'From idea to live product: AI development, SaaS/MVP, custom software, and mobile apps. Istanbul-based product studio from discovery through production.');

  // Pages may already carry the brand in their title; appending again wastes SERP characters.
  const fullTitle = title
    ? title.toLowerCase().includes(SITE_NAME.toLowerCase())
      ? title
      : `${title} | ${SITE_NAME}`
    : defaultTitle;
  const metaDesc = description || defaultDesc;
  const seoUrls = getSeoUrls({ canonicalPath: logicalPath, locale: lang });
  const canonicalUrl = seoUrls.canonicalUrl;
  const alternateLinks =
    localeAlternatePaths && SEO_LOCALE_URL_STRATEGY === 'path'
      ? (['tr', 'en'] as const)
          .filter((locale) => localeAlternatePaths[locale])
          .map((locale) => ({
            hrefLang: locale,
            href: buildAbsoluteUrl(SITE_URL, localizePath(localeAlternatePaths[locale]!, locale)),
          }))
      : seoUrls.alternateLinks;
  const xDefaultUrl = localeAlternatePaths?.tr
    ? buildAbsoluteUrl(SITE_URL, localizePath(localeAlternatePaths.tr, 'tr'))
    : localeAlternatePaths?.en
      ? buildAbsoluteUrl(SITE_URL, localizePath(localeAlternatePaths.en, 'en'))
      : seoUrls.xDefaultUrl;
  const isTemplateOgImage = !ogImage && !noindex && hasTemplateOgImage(logicalPath);
  const image = ogImage
    ? (ogImage.startsWith('http') ? ogImage : buildAbsoluteUrl(SITE_URL, ogImage))
    : isTemplateOgImage
      ? buildAbsoluteUrl(SITE_URL, templateOgImagePath(new URL(canonicalUrl).pathname))
      : SEO_DEFAULT_OG_IMAGE;
  const isDefaultOgImage = image === SEO_DEFAULT_OG_IMAGE;
  const resolvedOgImageAlt =
    ogImageAlt?.trim() ||
    (isTemplateOgImage
      ? buildTemplateOgImageAlt({ locale: lang, pageTitle: fullTitle })
      : isDefaultOgImage
        ? buildDefaultOgImageAlt({ locale: lang, pageTitle: fullTitle, isHome: logicalPath === '/' })
        : buildCustomOgImageAlt({ locale: lang, pageTitle: fullTitle }));
  const resolvedOgWidth =
    ogImageWidth ??
    (isTemplateOgImage ? OG_TEMPLATE_IMAGE_WIDTH : isDefaultOgImage ? SEO_DEFAULT_OG_IMAGE_WIDTH : undefined);
  const resolvedOgHeight =
    ogImageHeight ??
    (isTemplateOgImage ? OG_TEMPLATE_IMAGE_HEIGHT : isDefaultOgImage ? SEO_DEFAULT_OG_IMAGE_HEIGHT : undefined);
  const resolvedOgType =
    ogImageType ??
    (isTemplateOgImage
      ? OG_TEMPLATE_IMAGE_TYPE
      : isDefaultOgImage
        ? SEO_DEFAULT_OG_IMAGE_TYPE
        : inferImageMimeType(image));
  const orgSchema = buildOrganizationSchema(metaDesc, lang);
  const professionalServiceSchema = buildProfessionalServiceSchema(metaDesc, lang);
  const webSiteSchema = buildWebSiteSchema(lang);
  const webPageSchema = buildWebPageSchema({
    title: fullTitle,
    description: metaDesc,
    canonicalPath: logicalPath,
    inLanguage: lang === 'tr' ? 'tr-TR' : 'en-US',
    schemaType: inferPageSchemaType(logicalPath, type),
    locale: lang,
  });
  const customJsonLdList = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
  const homeAgentReadinessNodes =
    logicalPath === '/' ? buildHomeAgentReadinessNodes(lang) : [];
  const rawSeoGraph = buildSeoGraph([
    orgSchema,
    professionalServiceSchema,
    webSiteSchema,
    webPageSchema,
    ...homeAgentReadinessNodes,
    ...customJsonLdList,
  ]);
  const seoGraph = lang === 'tr' ? localizeJsonLdUrls(rawSeoGraph, 'tr') : rawSeoGraph;

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={metaDesc} />
      <meta name="author" content={authorName?.trim() || 'Figtures'} />
      <meta
        name="robots"
        content={noindex === 'follow' ? 'noindex, follow' : noindex ? 'noindex, nofollow' : 'index, follow'}
      />
      {googleSiteVerification && <meta name="google-site-verification" content={googleSiteVerification} />}
      {bingSiteVerification && <meta name="msvalidate.01" content={bingSiteVerification} />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDesc} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={resolvedOgImageAlt} />
      {resolvedOgWidth ? <meta property="og:image:width" content={String(resolvedOgWidth)} /> : null}
      {resolvedOgHeight ? <meta property="og:image:height" content={String(resolvedOgHeight)} /> : null}
      {resolvedOgType ? <meta property="og:image:type" content={resolvedOgType} /> : null}
      <meta property="og:site_name" content="Figtures" />
      <meta property="og:locale" content={lang === 'tr' ? 'tr_TR' : 'en_US'} />
      {alternateLinks
        .filter((alternate) => alternate.hrefLang !== lang)
        .map((alternate) => (
          <meta
            key={`og-alt-${alternate.hrefLang}`}
            property="og:locale:alternate"
            content={alternate.hrefLang === 'tr' ? 'tr_TR' : 'en_US'}
          />
        ))}
      <meta property="og:url" content={canonicalUrl} />
      {type === 'article' && publishedTime ? (
        <meta property="article:published_time" content={publishedTime} />
      ) : null}
      {type === 'article' && modifiedTime ? (
        <meta property="article:modified_time" content={modifiedTime} />
      ) : null}
      {type === 'article' && articleAuthorUrl ? (
        <meta property="article:author" content={articleAuthorUrl} />
      ) : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDesc} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={resolvedOgImageAlt} />
      <meta name="twitter:url" content={canonicalUrl} />

      {/* Canonical & hreflang */}
      <link rel="canonical" href={canonicalUrl} />
      {alternateLinks.map((alternate) => (
        <link key={alternate.hrefLang} rel="alternate" hrefLang={alternate.hrefLang} href={alternate.href} />
      ))}
      {xDefaultUrl && <link rel="alternate" hrefLang="x-default" href={xDefaultUrl} />}
      {logicalPath === '/' && (
        <link
          rel="alternate"
          type="application/ld+json"
          href={`${SITE_URL}/entity${lang === 'tr' ? '-tr' : ''}.jsonld`}
        />
      )}

      {/* JSON-LD Structured Data (@graph) */}
      <script type="application/ld+json">{JSON.stringify(seoGraph)}</script>
    </Helmet>
  );
};

export default SEOHead;
